/**
 * The review's header card (design "SoundGuys Review Header", 2026-10): hero + specs + store
 * button, the SoundGuys rating, "Measured in our lab" (noise cancelling and battery from the
 * review's scores, MDAQS), the bottom line with pros and cons, and the editor-scored rest as
 * "Our assessment". Below `lg` the hero is its own card above the rest and the specs move to
 * the end; at `lg` the hero (badge and rating over it), bottom line and button sit beside the
 * lab and pros/cons, with the specs and assessment as full-width rows beneath.
 *
 * Versus (2026-10-23): "Compare these scores with" under the lab cards offers the product's
 * curated lab peers (`rivals`) and a search for any other. Picking one pins the reviewed product
 * and the rival to the page's lab comparison and opens its side panel, the one the review's
 * product card pins to, drawn as the comparison (`ReviewVersusPanel`). The header stays the
 * review's own. Page state only.
 */
import clsx from 'clsx'
import dynamic from 'next/dynamic'
import { useCallback, useState } from 'react'
import { publicConfig } from 'config'
import { htmlReactParse } from 'shared-code/html'
import Linked from 'shared-components/service/Linked'
import AppImage from 'shared-components/ui/AppImage'
import CreditLink from 'shared-components/ui/CreditLink'
import IconedListItem from 'shared-components/ui/IconedListItem'
import Score from 'shared-components/ui/Score'
import ScoreRing from 'shared-components/ui/ScoreRing'
import OwnerHint from 'components/owner-notes/OwnerHint'
import {
  REVIEW_RATING_KEYS,
  MDAQS_EXPLAINER_URL,
  MIN_MEASURED_PEERS,
  MOS_EXPLAINER_URL,
} from 'lib/dealhunt/constants'
import type { RatingDistributionsResponse, ReviewPrice, ReviewRival } from 'lib/dealhunt/types'
import { formatCents, relativeTime } from 'lib/dealhunt/url'
import { MAX_PINS, useLabCompare } from 'lib/owner-notes/lab-compare'
import { scrollToAnchor } from 'lib/scroll-to-anchor'
import type { DSComplexScore, DSReviewProductPart } from 'shared-definitions/types'
import Check from 'shared-svg/Check.svg'
import Close from 'shared-svg/CloseBold.svg'
import styles from './ReviewHeader.module.css'

// TODO: "See ANC/battery rankings" and "Compare sound" links once those pages exist (2026-10-12).

// The compare page's picker, loaded only when a reader searches: most never do.
const AddCompareDialog = dynamic(() => import('components/compare/AddCompareDialog'), {
  ssr: false,
})

const LAB_CARDS = [
  { match: /noise cancel/i, label: 'Noise cancelling', keys: REVIEW_RATING_KEYS.noiseCancelling },
  { match: /battery/i, label: 'Battery life', keys: REVIEW_RATING_KEYS.battery },
] as const

/** `score` is null when the review has no such score: the card stays as a placeholder. */
type LabCard = (typeof LAB_CARDS)[number] & { score: number | null }

/**
 * Headphones, earbuds and headsets — the products the lab measures (ANC, battery, MDAQS), so a
 * missing score is a placeholder. Speakers, mics and the rest only show the cards they have.
 * Read from breadcrumb labels and DealHunt's product slug (`electronics/audio/earbuds/…`): WP
 * often files a review under "Reviews" only. With neither, the review keeps the legacy header.
 */
export function isListeningGear(
  breadcrumbs: readonly { label: string }[],
  productSlug?: string
): boolean {
  return [...breadcrumbs.map(c => c.label), productSlug ?? ''].some(s =>
    /earbud|headphone|headset/i.test(s)
  )
}

/** Editorial score labels as readers should see them; the data keeps its own. */
const ASSESSMENT_LABELS: Record<string, string> = { Feature: 'Features' }

/** The review's scores split into the lab cards and the editor-scored rest. */
export function splitScores(scores: readonly DSComplexScore[]): {
  lab: LabCard[]
  assessment: DSComplexScore[]
} {
  const isLab = (s: DSComplexScore): boolean => LAB_CARDS.some(c => c.match.test(s.label))
  const lab = LAB_CARDS.map(card => ({
    ...card,
    score: scores.find(s => card.match.test(s.label))?.ourScore ?? null,
  }))
  return { lab, assessment: scores.filter(s => !isLab(s)) }
}

export interface Standing {
  /** Where the class average sits on the score's own scale, 0–1. */
  avgFraction: number
  pill: 'Above avg' | 'Below avg' | 'At avg'
  /** "Beats 85% of over-ear headphones"; null when DealHunt has no percentile. */
  beats: string | null
}

/**
 * The score's standing in its class, from DealHunt's rating distributions — the same gate
 * as the PDP's scores card (`MIN_MEASURED_PEERS` measured peers). Null, too few peers or no
 * merged value → null, and the card shows no pill, marker or line.
 */
export function standingOf(
  response: RatingDistributionsResponse | null,
  keys: readonly string[]
): Standing | null {
  const dist = response?.distributions.find(d => keys.includes(d.ratingType))
  if (!dist || dist.value === null || dist.known < MIN_MEASURED_PEERS) {
    return null
  }
  const { min, max, avg } = dist.stats
  // The pill follows the rank, so it never contradicts "Beats X%"; the mean only without one.
  const [score, middle] = dist.percentile !== null ? [dist.percentile, 50] : [dist.value, avg]
  let pill: Standing['pill'] = 'At avg'
  if (score > middle) {
    pill = 'Above avg'
  } else if (score < middle) {
    pill = 'Below avg'
  }
  // A widened score names ITS class (On-Ear → Headphones), not the response's.
  const peers = dist.class?.name ?? response?.class?.name
  return {
    avgFraction: (avg - min) / (max - min),
    pill,
    beats:
      dist.percentile !== null && peers
        ? `Beats ${dist.percentile}% of ${peers.toLowerCase()}`
        : null,
  }
}

const clamp = (n: number): number => Math.max(0, Math.min(1, n))

const Bar: React.VFC<{
  fraction: number
  marker?: number
  className?: string
}> = ({ fraction, marker, className }) => (
  <span className={clsx(styles.bar, className)} aria-hidden="true">
    <span style={{ width: `${clamp(fraction) * 100}%` }} />
    {marker !== undefined && (
      <span className={styles.avgMarker} style={{ left: `${clamp(marker) * 100}%` }} />
    )}
  </span>
)

const Pill: React.VFC<{ standing: Standing | null }> = ({ standing }) => {
  if (!standing) {
    return null
  }
  return (
    <span className={clsx(styles.pill, standing.pill === 'Above avg' && styles.pillAbove)}>
      {standing.pill}
    </span>
  )
}

const MDAQS_HINT = {
  text: (
    <>
      {'MDAQS is a '}
      <a href={MOS_EXPLAINER_URL}>{'Mean Opinion Score'}</a>
      {
        ' for audio quality captured by HEAD acoustics, trained on data collected on a large aggregate of listeners - aimed at capturing the opinion of the average listener.'
      }
    </>
  ),
  href: MDAQS_EXPLAINER_URL,
}

/** The MDAQS card's title, with a "?" that explains the score and links to the explainer. */
const MdaqsTitle: React.VFC = () => (
  <span className={styles.mdaqsTitle}>
    <span className={styles.eyebrowMuted}>{'Multi-Dimensional Audio Quality Score'}</span>
    <OwnerHint hint={MDAQS_HINT} className={styles.help}>
      <span aria-hidden="true">{'?'}</span>
      <span className={styles.srOnly}>{'What is MDAQS?'}</span>
    </OwnerHint>
  </span>
)

/** The line under the pills while a pick is under way or refused. */
const SEARCH_NOTES = {
  loading: 'Loading that product…',
  failed: 'Couldn’t load that product. Try another.',
} as const

/** A lab score this product has no measurement for. */
const Unmeasured: React.VFC<{ title: React.ReactNode }> = ({ title }) => (
  <>
    {title}
    <span className={styles.value}>{'—'}</span>
    <Bar fraction={0} />
    <span className={styles.muted}>{'Not measured for this product'}</span>
  </>
)

interface ReviewHeaderProps {
  review: Pick<
    DSReviewProductPart,
    | 'image'
    | 'badges'
    | 'features'
    | 'storeButton'
    | 'device'
    | 'scores'
    | 'mdaqs'
    | 'bottomLineContent'
    | 'table'
  >
  /** DealHunt class rankings for the lab cards; null renders the cards without them. */
  labDistributions?: RatingDistributionsResponse | null
  /** The review's lab charts; null hides "See all lab data" (a review without charts). */
  labDataHref?: string | null
  /** The reviewed product as a lab-comparison pin; null (no DealHunt product) hides the versus
   *  box, as does a page without the lab-compare provider. */
  self?: ReviewRival | null
  /** "Compare these scores with" choices before the reader searches for more. */
  rivals?: readonly ReviewRival[]
  /** The PDP's current price, shown beside the store button; null shows the button alone. */
  price?: ReviewPrice | null
  className?: string
}

const ReviewHeader: React.VFC<ReviewHeaderProps> = ({
  review,
  labDistributions = null,
  labDataHref = null,
  self = null,
  rivals = [],
  price = null,
  className,
}) => {
  const labCompare = useLabCompare()
  const [picking, setPicking] = useState(false)
  const [searchState, setSearchState] = useState<'idle' | 'loading' | 'failed'>('idle')
  // Stable: the picker's effect keys on it, and re-running that effect loses the page's scroll.
  const closeSearch = useCallback(() => setPicking(false), [])
  const { image, badges, features, storeButton, device, scores, mdaqs, bottomLineContent } = review
  const { lab, assessment } = splitScores(scores)
  const [badge] = badges
  const mdaqsOverall = mdaqs?.scores.find(s => /overall/i.test(s.name)) ?? null
  const mdaqsParts = mdaqs?.scores.filter(s => s !== mdaqsOverall) ?? []
  const mdaqsFraction = (value: number): number => (value - 1) / ((mdaqs?.maxValue ?? 5) - 1)
  // Only listening gear (`isListeningGear`) gets this header, so a missing lab score is a
  // placeholder. ANC and battery placeholders only sit beside real measurements: with none at
  // all (often a wired pair, no battery or ANC to measure) the MDAQS placeholder stands alone.
  const measured = lab.some(card => card.score !== null) || mdaqs !== null
  const labCards = measured ? lab : []
  const mdaqsStanding = standingOf(labDistributions, REVIEW_RATING_KEYS.mdaqs)
  const overallStanding = standingOf(labDistributions, REVIEW_RATING_KEYS.overall)
  const priceLabel = price ? formatCents(price.cents, price.domain) : null
  const checked = relativeTime(price?.checkedAt)
  const trailingSlash = publicConfig('useTrailingSlash')
  // The reviewed product first, so it keeps the comparison's first colour — even when it is the
  // reader's declared pair, since the review's panel draws the pins alone. Room is checked for
  // both before either is pinned: a comparison without room for both becomes just this pair
  // (2026-10-26). A rival already in the comparison is a toggle: clicking it again unpins it.
  const compareWith = (rival: ReviewRival): void => {
    if (!self || !labCompare) {
      return
    }
    if (labCompare.isPinned(rival.nodeId)) {
      labCompare.unpin(rival.nodeId)
      return
    }
    const pair = [self, rival]
    const missing = pair.filter(t => !labCompare.isPinned(t.nodeId))
    if (labCompare.pinned.length + missing.length > MAX_PINS) {
      setSearchState('idle')
      labCompare.clearPins(pair)
      return
    }
    setSearchState('idle')
    for (const t of missing) {
      labCompare.pin(t)
    }
  }
  const pick = async (id: string): Promise<void> => {
    setPicking(false)
    const curated = rivals.find(r => r.nodeId === id)
    if (curated) {
      compareWith(curated)
      return
    }
    setSearchState('loading')
    let picked: ReviewRival | null = null
    try {
      // Loaded with the search, not the page: most readers never search.
      const { loadRival } = await import('lib/dealhunt/compare')
      picked = await loadRival(id, trailingSlash)
    } catch {
      // The chunk failed to load: as a product that won't resolve.
    }
    if (!picked) {
      setSearchState('failed')
      return
    }
    setSearchState('idle')
    compareWith(picked)
  }

  return (
    <div className={clsx(styles.root, className)}>
      {image ? (
        <div className={styles.hero}>
          <AppImage
            eager
            layout="fill"
            image={image}
            imageClassName={styles.heroImage}
            sizes={[{ bp: 'lg', val: 640 }, 'calc(100vw - rem(30))']}
          />
          {image.credits.length ? (
            <div className={styles.credits}>
              {image.credits.map((item, index) => (
                <CreditLink key={index} credit={item} />
              ))}
            </div>
          ) : null}
        </div>
      ) : null}
      {badge ? (
        <AppImage
          layout="raw"
          image={badge}
          sizes={64}
          className={styles.badge}
          imageClassName={styles.badgeImage}
        />
      ) : null}

      <div className={styles.card}>
        {device.ourScore ? (
          <div className={styles.rating}>
            <ScoreRing className={styles.ring} score={device.ourScore} />
            <div className={styles.ratingText}>
              <span className={styles.eyebrow}>{'SoundGuys rating'}</span>
              <Score score={device.ourScore} />
              <span className={styles.muted}>{'Based on our lab testing'}</span>
              {overallStanding?.beats && (
                <span className={styles.muted}>{overallStanding.beats}</span>
              )}
            </div>
          </div>
        ) : null}

        <section className={clsx(styles.lab, styles.ruled)}>
          <div className={styles.sectionHead}>
            <h2 className={styles.h3}>{'Measured in our lab'}</h2>
            {labDataHref ? (
              <a href={labDataHref} className={styles.link} onClick={scrollToAnchor}>
                <span className={styles.desktopOnly}>{'See all lab data →'}</span>
                <span className={styles.mobileOnly}>{'All lab data →'}</span>
              </a>
            ) : null}
          </div>
          <div className={styles.labGrid}>
            {labCards.map(card => {
              if (card.score === null) {
                return (
                  <div key={card.label} className={styles.labCard}>
                    <Unmeasured title={<span className={styles.eyebrowMuted}>{card.label}</span>} />
                  </div>
                )
              }
              const standing = standingOf(labDistributions, card.keys)
              return (
                <div key={card.label} className={styles.labCard}>
                  <span className={styles.eyebrowMuted}>{card.label}</span>
                  <span className={styles.valueRow}>
                    <span className={styles.value}>
                      {card.score.toFixed(1)}
                      <small>{'/10'}</small>
                    </span>
                    <Pill standing={standing} />
                  </span>
                  <Bar fraction={card.score / 10} marker={standing?.avgFraction} />
                  {standing?.beats && <span className={styles.muted}>{standing.beats}</span>}
                </div>
              )
            })}
            {mdaqs ? (
              <div className={clsx(styles.labCard, styles.mdaqs)}>
                <div className={styles.mdaqsOverall}>
                  <MdaqsTitle />
                  {mdaqsOverall ? (
                    <>
                      <span className={styles.valueRow}>
                        <span className={styles.value}>
                          {mdaqsOverall.value.toFixed(1)}
                          <small>{`/${mdaqs.maxValue}`}</small>
                        </span>
                        <Pill standing={mdaqsStanding} />
                      </span>
                      <Bar
                        fraction={mdaqsFraction(mdaqsOverall.value)}
                        marker={mdaqsStanding?.avgFraction}
                      />
                      {mdaqsStanding?.beats && (
                        <span className={styles.muted}>{mdaqsStanding.beats}</span>
                      )}
                    </>
                  ) : null}
                </div>
                <div className={styles.mdaqsParts}>
                  {mdaqsParts.map(part => (
                    <div key={part.name} className={styles.mdaqsPart}>
                      <span className={styles.partValue}>{part.value.toFixed(1)}</span>
                      <Bar fraction={mdaqsFraction(part.value)} />
                      <span className={styles.muted}>{part.name}</span>
                    </div>
                  ))}
                </div>
                <div className={styles.mdaqsFoot}>
                  <span className={styles.muted}>
                    {`Predicted listener score from a panel model, 1 (very bad) to ${mdaqs.maxValue} (very good)`}
                  </span>
                </div>
              </div>
            ) : (
              <div className={clsx(styles.labCard, styles.mdaqs)}>
                <div className={styles.mdaqsOverall}>
                  <Unmeasured title={<MdaqsTitle />} />
                </div>
              </div>
            )}
          </div>
          {self && labCompare ? (
            <div className={styles.versus}>
              <span className={styles.versusLabel}>{'Compare these scores with'}</span>
              <div className={styles.versusPills}>
                {rivals.map(r => (
                  <button
                    key={r.nodeId}
                    type="button"
                    data-pinned={labCompare.isPinned(r.nodeId) ? '' : undefined}
                    className={styles.versusPill}
                    onClick={() => compareWith(r)}
                  >
                    {r.name}
                    {labCompare.isPinned(r.nodeId) ? (
                      <span className={styles.srOnly}>{' (in your comparison)'}</span>
                    ) : null}
                  </button>
                ))}
                <button
                  type="button"
                  className={clsx(styles.versusPill, styles.versusAdd)}
                  onClick={() => setPicking(true)}
                >
                  <span aria-hidden="true">{'+'}</span>
                  {'Search another product'}
                </button>
              </div>
              {searchState !== 'idle' ? (
                <span className={styles.muted} role="status">
                  {SEARCH_NOTES[searchState]}
                </span>
              ) : null}
              {picking ? (
                <AddCompareDialog
                  added={new Set([self.nodeId, ...labCompare.pinned.map(p => p.nodeId)])}
                  onPick={id => void pick(id)}
                  onClose={closeSearch}
                />
              ) : null}
            </div>
          ) : null}
        </section>

        <div className={clsx(styles.verdict, styles.ruled)}>
          <div className={styles.bottomLine}>
            <h2 className={styles.h3}>{bottomLineContent.title || 'The bottom line'}</h2>
            <p className={styles.bottomText}>{bottomLineContent.text}</p>
          </div>
          <div className={clsx(styles.evaluates, styles.positive)}>
            <h3 className={styles.h4}>{review.table.positive.title}</h3>
            {review.table.positive.list.map((item, index) => (
              <IconedListItem icon={Check} key={index} iconClassName={styles.iconPositive}>
                {item}
              </IconedListItem>
            ))}
          </div>
          <div className={styles.evaluates}>
            <h3 className={styles.h4}>{review.table.negative.title}</h3>
            {review.table.negative.list.map((item, index) => (
              <IconedListItem icon={Close} key={index} iconClassName={styles.iconNegative}>
                {item}
              </IconedListItem>
            ))}
          </div>
        </div>

        {assessment.length ? (
          <div className={clsx(styles.assessment, styles.ruled)}>
            <h2 className={styles.h4}>{'Our assessment'}</h2>
            <span className={styles.muted}>{'Editor-scored from hands-on use, not measured'}</span>
            <dl className={styles.assessmentList}>
              {assessment.map(score => (
                <div key={score.label} className={styles.assessmentRow}>
                  <dt>{ASSESSMENT_LABELS[score.label] ?? score.label}</dt>
                  <dd>{score.ourScore.toFixed(1)}</dd>
                  <Bar fraction={score.ourScore / 10} className={styles.assessmentBar} />
                </div>
              ))}
            </dl>
          </div>
        ) : null}

        {features.length ? (
          <dl className={clsx(styles.specs, styles.ruled)}>
            {features.map((feature, index) => (
              <div key={index}>
                <dt className={styles.muted}>{feature.desc}</dt>
                <dd>
                  {feature.val.map((v, i) => (
                    <div key={i}>{htmlReactParse(v)}</div>
                  ))}
                </dd>
              </div>
            ))}
          </dl>
        ) : null}

        {storeButton ? (
          <div className={styles.store}>
            <Linked {...storeButton.link.pLink} className={styles.storeButton}>
              {`${storeButton.link.label} →`}
            </Linked>
            {priceLabel ? (
              <span className={styles.storePrice}>
                <strong>{priceLabel}</strong>
                {checked ? (
                  <span className={styles.muted}>{`Price last checked ${checked}`}</span>
                ) : null}
              </span>
            ) : null}
          </div>
        ) : null}
      </div>
    </div>
  )
}

export default ReviewHeader
